<template>
  <div class="company-map ml-4 mr-4">
    <div v-if="type === 'create'">
      <div class="row py-3 align-items-center">
        <div class="col company-h1">
          <button type="button"
                  class="btn btn-back"
                  @click="setCompanyStep('companies-info')"
          />
          Create Company
        </div>
        <div class="col text-right">
          <button type="button"
                  class="btn btn-primary ld-ext-right"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
      <companies-steps-menu type="create" />
    </div>
    <div v-if="type === 'edit'">
      <div class="row py-3 align-items-center company-header">
        <div class="col-4">
          <h2 class="company-h1 mr-3">
            <button type="button"
                    class="btn btn-back"
                    @click="$router.push('/companies')"
            />
            {{ company.name }}
          </h2>
          <template v-if="company.status">
            <span v-if="company.status.name === 'Active'"
                  :class="'btn-status btn-active'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Inactive'"
                  :class="'btn-status btn-closed'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Setup Incomplete'"
                  :class="'btn-status btn-ncover info-company'"
            >{{ company.status.name }}
              <div class="btn btn-link p-0 tippy-popup tippy-info" @mouseover="showTooltipPrefix = true"
                   @mouseout="showTooltipPrefix = false"
              >
                <i class="fa fa-info-circle" />
              </div>
              <div class="popover tippy-info-popover" v-if="showTooltipPrefix">
                Setup incomplete is present when the company doesn't have one or more of the following: move sizes, arrival windows, merchant account, rates and coverage.
              </div>
            </span>
          </template>
        </div>
        <div class="col-8 d-flex align-items-center justify-content-end">
          <companies-visibility-bar
            :company="company"
            @saved="$emit('saved')"
            v-if="$_can('companies.review.request')"
          />
          <button type="button"
                  class="btn btn-primary ld-ext-right ml-5"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
                  @click="save"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
    </div>

    <div class="mb-3 w-100">
      <h5 class="companies-sub-page-title">Company licence</h5>
      <p class="description-text">
        Please provide your company's licence number and add a comment if you wish to provide additional information.
      </p>
      <div class="row l-m-b-16">
        <div class="col-6">
          <h2 class="l-header-2">Licence details</h2>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Licence number</label>
                <input v-model="licence.licence_number"
                       type="text"
                       autocomplete="no"
                       class="form-control"
                       placeholder="Enter licence number"
                       :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="licence_number" />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Expiration date</label>
                <flat-pickr v-model="licence.expiration_date"
                            :config="config"
                            class="form-control"
                            placeholder="Select date"
                            red="flatPickr"
                            data-input
                />
              </div>
              <error-field :errors="errors" field="expiration_date" />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col pr-1">
              <div class="select-wrap">
                <label>Note</label>
                <textarea v-model="licence.note"
                          type="text"
                          autocomplete="no"
                          class="form-control licence-note"
                          placeholder="Enter comment"
                          :disabled="!$_can('companies.update')"
                />
              </div>
              <error-field :errors="errors" field="note" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import axios from 'axios';
import toastr from 'toastr';
import CompaniesStepsMenu from '../CompaniesStepsMenu';
import CompaniesVisibilityBar from '../CompaniesVisibilityBar';
import Errors from '../../../classes/Errors';

export default {
  components: { CompaniesStepsMenu, CompaniesVisibilityBar, flatPickr },
  props: {
    type: {
      type: String,
      default: 'edit',
    },
    company: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      assignmentTypes: [],
      buttonDisabled: false,
      config: {
        dateFormat: 'm/j/Y',
        wrap: true,
        minDate: 'today',
      },
      errors: new Errors(),
      licence: {
        expiration_date: '',
        licence_number: '',
        note: '',
      },
      loadingButton: false,
      showTooltipPrefix: false,
    };
  },
  watch: {
    company() {
      this.setLicenceFromCompany(this.company);
    },
  },
  mounted() {
    this.setLicenceFromCompany(this.company);
  },
  methods: {
    ...mapMutations(['setCompanyStep']),
    setLicenceFromCompany(company) {
      if (company.licence) {
        this.licence = company.licence;
      } else {
        this.licence = {
          licence_number: '',
          note: '',
        };
      }
    },
    save() {
      if (this.licence.id) {
        axios.patch(`/companies/${this.company.id}/licences`, this.licence).then(() => {
          this.$emit('saved');
          toastr.success('Successfully updated company licence');
        });
      } else {
        axios.post(`/companies/${this.company.id}/licences`, this.licence).then(() => {
          this.$emit('saved');
          toastr.success('Successfully created company licence');
        });
      }
    },
  },
};
</script>

<style scoped>
.description-text {
  margin-top: 15px;
  width: 50%;
}

.licence-note {
  min-height: 100px;
}
</style>



// WEBPACK FOOTER //
// src/components/companies/mod24settings/CompaniesLicence.vue